import React from 'react';

import moment from 'moment';
import {MomentInput} from 'moment-tz';
import RRule from 'rrule';

import {HeadingText} from 'app/components/general/HeadingText';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {TooltipList} from 'app/components/general/TooltipList';
import {useCurrentUserState} from 'app/current-user/api';
import {getRepetitionLabel, localiseDate} from 'app/tasks/utils';
import {gt} from 'app/utils/i18n';

interface Props {
  rrule: RRule;
}

function toUTCAndStripTimezone(date: Date) {
  return date.toISOString().replace('.000Z', '');
}

export function getLocalDateWithoutTimezone(
  utcDate: MomentInput,
  timezone: string
) {
  const localizedMoment = localiseDate(utcDate, timezone);

  return new Date(
    Date.UTC(
      localizedMoment.year(),
      localizedMoment.month(),
      localizedMoment.date(),
      localizedMoment.hour(),
      localizedMoment.minutes()
    )
  );
}

function rruleInFakeUTC(utcRRule: RRule, timezone: string): RRule {
  return new RRule({
    ...utcRRule.origOptions,
    dtstart: getLocalDateWithoutTimezone(
      utcRRule.origOptions.dtstart,
      timezone
    ),
  });
}

export function RRuleRepetitionLabel({rrule}: Props): JSX.Element {
  const {timezone} = useCurrentUserState().data!;

  const label = React.useMemo(() => getRepetitionLabel('', rrule), [rrule]);
  const isOnceOnly = rrule.options.count === 1;
  const timezoneRRule = rruleInFakeUTC(rrule, timezone);

  return (
    <div className="border-top py-4">
      <div className="col-12 text-center">
        <HeadingText className="mr-1" light>
          {gt('Task repetition')}:
        </HeadingText>
        <b>{label}</b>
      </div>
      {!isOnceOnly ? (
        <>
          <div className="col-12 mt-2 text-center">
            <HeadingText className="mr-1" light>
              {gt('First occurrence')}:
            </HeadingText>
            <LazyTooltip
              hide={isOnceOnly}
              tooltip={
                <TooltipList
                  title={gt('First 10 occurrences:')}
                  items={timezoneRRule
                    .all((_, i) => i < 10)
                    .map((date) => {
                      const dateStr = moment
                        .tz(toUTCAndStripTimezone(date), timezone)
                        .format('llll');
                      return {id: dateStr, name: dateStr};
                    })}
                />
              }
            >
              <span
                style={{borderBottom: '1px dotted #666'}}
                className="font-weight-bold"
              >
                {moment
                  .tz(
                    toUTCAndStripTimezone(
                      timezoneRRule.all((_, i) => i < 1)[0]
                    ),
                    timezone
                  )
                  .format('ll')}
              </span>
            </LazyTooltip>
          </div>
        </>
      ) : null}
    </div>
  );
}
